import { useState, useEffect, useCallback } from "react";
import { Button, Card, Checkbox, Col, Form, Input, Row, Select, Space, Switch, Typography, Upload } from "antd";
import { useHistory } from "react-router-dom";
import axios from "axios";
import { message } from "@bindeleddet/core/utils";
import { Sector } from "@bindeleddet/core/types";
import { uploadButton } from "components/UploadButton/UploadButton";
import { IMAGE_ACCEPT, beforeUploadImage } from "helpers/fileValidation";
import { PASSWORD_PATTERN } from "helpers/password";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

const { Title, Text } = Typography;

type FormValues = {
  name: string;
  sectors: number[];
  size: number;
  description?: string;
  logo?: File;
  logo_wide?: File;
  url?: string;
  organization_number: string;
  visitor_address?: string;
  visitor_area_code?: string;
  visitor_region?: string;
  country?: string;
  telephone?: string;
  email?: string;
  register_description?: string;
  username: string;
  password: string;
  confirm: string;
  approved_for_booking: boolean;
  is_relevant: boolean;
  first_name?: string;
  last_name?: string;
  companyContactTelephone?: string;
  companyContactEmail?: string;
  position?: string;
};

const usernameRule = /^[a-zæøå]+$/;

const CreateCompanyProfileAdmin = () => {
  const [form] = Form.useForm<FormValues>();
  const history = useHistory();
  const [loading, setLoading] = useState(false);
  const [includeContact, setIncludeContact] = useState(true);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [coverFile, setCoverFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [coverPreview, setCoverPreview] = useState<string | null>(null);
  const [sectors, setSectors] = useState<Sector[]>([]);

  useEffect(() => {
    form.setFieldsValue({
      approved_for_booking: true,
      is_relevant: true,
      register_description: "Opprettet av admin",
    });
  }, [form]);

  useEffect(() => {
    axios.get<Sector[]>("/companies/sectors/").then(response => setSectors(response.data));
  }, []);

  const resetContactFields = useCallback(() => {
    form.setFieldsValue({
      first_name: undefined,
      last_name: undefined,
      companyContactTelephone: undefined,
      companyContactEmail: undefined,
      position: undefined,
    });
  }, [form]);

  const onToggleContact = () => {
    const next = !includeContact;
    setIncludeContact(next);
    if (!next) {
      resetContactFields();
    }
  };

  const onFinish = async (values: FormValues) => {
    setLoading(true);
    try {
      const formData = new FormData();
      const appendBool = (key: string, val?: boolean) => {
        if (val === undefined || val === null) return;
        formData.append(key, val ? "true" : "false");
      };

      const appendIf = (key: string, val: unknown) => {
        if (val === undefined || val === null) return;
        formData.append(key, String(val));
      };

      appendIf("name", values.name);
      if (values.sectors) values.sectors.forEach(s => formData.append("sectors", String(s)));
      appendIf("size", values.size);
      appendIf("description", values.description);
      appendIf("url", values.url);
      appendIf("organization_number", values.organization_number);
      appendIf("visitor_address", values.visitor_address);
      appendIf("visitor_area_code", values.visitor_area_code);
      appendIf("visitor_region", values.visitor_region);
      appendIf("country", values.country);
      appendIf("telephone", values.telephone);
      appendIf("email", values.email);
      appendIf("register_description", values.register_description);
      appendIf("username", values.username);
      appendIf("password", values.password);
      formData.append("approved", "true");
      appendBool("approved_for_booking", values.approved_for_booking ?? true);
      appendBool("is_relevant", values.is_relevant ?? true);

      if (logoFile) formData.append("logo", logoFile);
      if (coverFile) formData.append("logo_wide", coverFile);

      const companyResponse = await axios.post("/companies/", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (includeContact) {
        const contactData = {
          first_name: values.first_name,
          last_name: values.last_name,
          telephone: values.companyContactTelephone,
          email: values.companyContactEmail,
          position: values.position,
          company: companyResponse.data.id,
          main_contact: true,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        };
        await axios.post(`/companies/${companyResponse.data.id}/contact-persons/`, contactData);
      }

      message("success", "Bedrift opprettet");
      history.push(`/admin/companies/${companyResponse.data.id}`);
    } catch (error) {
      message("error", "Kunne ikke opprette bedrift");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Title level={2} style={{ marginBottom: 16 }}>
        Oprett bedrift
      </Title>

      <Form<FormValues> form={form} layout="vertical" onFinish={onFinish} scrollToFirstError>
        <Space direction="vertical" size="large" style={{ width: "100%" }}>
          <Card>
            <Title level={3} style={{ marginTop: 0 }}>
              Informasjon om bedriften
            </Title>
            <Form.Item
              label="Bedriftsnavn"
              name="name"
              rules={[{ required: true, message: "Skriv inn navn på bedriften" }, { max: 255 }]}
            >
              <Input maxLength={255} />
            </Form.Item>

            <Form.Item
              label="Bransje(r)"
              name="sectors"
              rules={[
                { required: true, message: "Velg minst én bransje" },
                () => ({
                  validator(_, value) {
                    if (value && value.length > 4) return Promise.reject(new Error("Velg maks fire bransjer"));
                    return Promise.resolve();
                  },
                }),
              ]}
            >
              <Checkbox.Group style={{ width: "100%" }}>
                <Row gutter={[8, 8]}>
                  {sectors
                    .filter(s => !!s.name)
                    .map(s => (
                      <Col key={s.id} lg={4} md={6} sm={8} xs={12}>
                        <Checkbox value={s.id}>{s.name}</Checkbox>
                      </Col>
                    ))}
                </Row>
              </Checkbox.Group>
            </Form.Item>

            <Row gutter={16}>
              <Col md={12} sm={24} xs={24}>
                <Form.Item
                  label="Antall ansatte"
                  name="size"
                  rules={[{ required: true, message: "Oppgi antall ansatte" }]}
                >
                  <Select
                    placeholder="Velg antall ansatte"
                    options={[
                      { label: "1-10", value: 1 },
                      { label: "11-50", value: 2 },
                      { label: "51-200", value: 3 },
                      { label: "201-500", value: 4 },
                      { label: "501-1000", value: 5 },
                      { label: "1001-5000", value: 6 },
                      { label: "5001-10 000", value: 7 },
                      { label: "10 001+", value: 8 },
                    ]}
                  />
                </Form.Item>
              </Col>
              <Col md={12} sm={24} xs={24}>
                <Form.Item label="Nettside" name="url" rules={[{ max: 255, message: "Maks 255 tegn" }]}>
                  <Input addonBefore="https://" maxLength={255} />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item label="Beskrivelse" name="description" getValueFromEvent={content => content}>
              <ReactQuill theme="snow" />
            </Form.Item>

            <Row gutter={16}>
              <Col md={12} sm={24} xs={24}>
                <Form.Item
                  label="Organisasjonsnummer"
                  name="organization_number"
                  rules={[
                    { required: true, message: "Skriv inn organisasjonsnummer" },
                    { pattern: /^[0-9]+$/, message: "Kun tall" },
                    { min: 9, max: 9, message: "Må være 9 siffer" },
                  ]}
                >
                  <Input maxLength={9} />
                </Form.Item>
              </Col>
              <Col md={12} sm={24} xs={24}>
                <Form.Item
                  label="E-postadresse"
                  name="email"
                  rules={[{ required: true, message: "Skriv inn e-postadresse" }, { type: "email" }, { max: 100 }]}
                >
                  <Input maxLength={100} />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col md={12} sm={24} xs={24}>
                <Form.Item label="Telefonnummer" name="telephone" rules={[{ max: 255 }]}>
                  <Input maxLength={255} />
                </Form.Item>
              </Col>
              <Col md={12} sm={24} xs={24}>
                <Form.Item label="Postadresse" name="visitor_address" rules={[{ max: 255 }]}>
                  <Input maxLength={255} />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col md={8} sm={24} xs={24}>
                <Form.Item label="Postkode" name="visitor_area_code" rules={[{ max: 255 }]}>
                  <Input maxLength={255} />
                </Form.Item>
              </Col>
              <Col md={8} sm={24} xs={24}>
                <Form.Item label="By/region" name="visitor_region" rules={[{ max: 255 }]}>
                  <Input maxLength={255} />
                </Form.Item>
              </Col>
              <Col md={8} sm={24} xs={24}>
                <Form.Item label="Land" name="country" rules={[{ max: 255 }]}>
                  <Input maxLength={255} />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col md={12} sm={24} xs={24}>
                <Form.Item label="Logo" name="logo_required" rules={[{ required: true, message: "Last opp logo" }]}>
                  <Upload
                    listType="picture-card"
                    accept={IMAGE_ACCEPT}
                    beforeUpload={file => {
                      const res = beforeUploadImage(file, "Logo må være et bilde");
                      if (res === Upload.LIST_IGNORE) return res;
                      setLogoFile(file);
                      setLogoPreview(URL.createObjectURL(file));
                      return false;
                    }}
                    onRemove={() => {
                      setLogoFile(null);
                      setLogoPreview(null);
                    }}
                    fileList={
                      logoPreview
                        ? [
                            {
                              uid: "-1",
                              name: "Logo",
                              status: "done",
                              url: logoPreview,
                            },
                          ]
                        : []
                    }
                  >
                    {uploadButton}
                  </Upload>
                </Form.Item>
              </Col>
              <Col md={12} sm={24} xs={24}>
                <Form.Item label="Coverbilde">
                  <Upload
                    listType="picture-card"
                    accept={IMAGE_ACCEPT}
                    beforeUpload={file => {
                      const res = beforeUploadImage(file, "Coverbilde må være et bilde");
                      if (res === Upload.LIST_IGNORE) return res;
                      setCoverFile(file);
                      setCoverPreview(URL.createObjectURL(file));
                      return false;
                    }}
                    onRemove={() => {
                      setCoverFile(null);
                      setCoverPreview(null);
                    }}
                    fileList={
                      coverPreview
                        ? [
                            {
                              uid: "-1",
                              name: "Coverbilde",
                              status: "done",
                              url: coverPreview,
                            },
                          ]
                        : []
                    }
                  >
                    {uploadButton}
                  </Upload>
                </Form.Item>
              </Col>
            </Row>
          </Card>

          <Card>
            <Row justify="space-between" align="middle" style={{ marginBottom: 8 }}>
              <Title level={3} style={{ margin: 0 }}>
                Kontaktperson
              </Title>
              <Button type="default" danger={includeContact} onClick={onToggleContact}>
                {includeContact ? "Ikke legg til kontaktperson" : "Legg til kontaktperson"}
              </Button>
            </Row>
            {includeContact && (
              <Space direction="vertical" style={{ width: "100%" }} size="middle">
                <Row gutter={16}>
                  <Col md={12} sm={24} xs={24}>
                    <Form.Item
                      label="Fornavn"
                      name="first_name"
                      rules={[{ required: true, message: "Skriv inn fornavn" }, { max: 255 }]}
                    >
                      <Input maxLength={255} />
                    </Form.Item>
                  </Col>
                  <Col md={12} sm={24} xs={24}>
                    <Form.Item
                      label="Etternavn"
                      name="last_name"
                      rules={[{ required: true, message: "Skriv inn etternavn" }, { max: 255 }]}
                    >
                      <Input maxLength={255} />
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={16}>
                  <Col md={8} sm={24} xs={24}>
                    <Form.Item
                      label="Telefonnummer"
                      name="companyContactTelephone"
                      rules={[{ required: true, message: "Skriv inn telefonnummer" }, { max: 255 }]}
                    >
                      <Input maxLength={255} />
                    </Form.Item>
                  </Col>
                  <Col md={8} sm={24} xs={24}>
                    <Form.Item
                      label="E-postadresse"
                      name="companyContactEmail"
                      rules={[
                        { required: true, message: "Skriv inn e-postadresse" },
                        { type: "email", message: "Ugyldig e-post" },
                        { max: 100 },
                      ]}
                    >
                      <Input maxLength={100} />
                    </Form.Item>
                  </Col>
                  <Col md={8} sm={24} xs={24}>
                    <Form.Item label="Stilling" name="position" rules={[{ max: 255 }]}>
                      <Input maxLength={255} />
                    </Form.Item>
                  </Col>
                </Row>
              </Space>
            )}
          </Card>

          <Card>
            <Title level={3} style={{ marginTop: 0 }}>
              Bruker
            </Title>
            <Row gutter={16}>
              <Col md={12} sm={24} xs={24}>
                <Form.Item
                  label="Brukernavn"
                  name="username"
                  rules={[
                    { required: true, message: "Skriv inn brukernavn" },
                    { pattern: usernameRule, message: "Kun små bokstaver (a-å)" },
                  ]}
                >
                  <Input maxLength={150} />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col md={12} sm={24} xs={24}>
                <Form.Item
                  label="Passord"
                  name="password"
                  rules={[
                    { required: true, message: "Vennligst fyll inn passord" },
                    { pattern: PASSWORD_PATTERN, message: "Minst 8 tegn, én stor bokstav og ett tall" },
                  ]}
                  hasFeedback
                >
                  <Input.Password />
                </Form.Item>
              </Col>
              <Col md={12} sm={24} xs={24}>
                <Form.Item
                  label="Bekreft passord"
                  name="confirm"
                  dependencies={["password"]}
                  hasFeedback
                  rules={[
                    { required: true, message: "Vennligst bekreft passordet" },
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        if (!value || getFieldValue("password") === value) return Promise.resolve();
                        return Promise.reject(new Error("De to passordene samsvarer ikke"));
                      },
                    }),
                  ]}
                >
                  <Input.Password />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col md={12} sm={24} xs={24}>
                <Form.Item
                  label="Beskrivelse for registrering"
                  name="register_description"
                  rules={[{ required: true, message: "Skriv en kort begrunnelse" }]}
                >
                  <Input.TextArea rows={3} />
                </Form.Item>
              </Col>
              <Col md={12} sm={24} xs={24}>
                <Card size="small" title="Tilganger">
                  <Space direction="vertical" size="small" style={{ width: "100%" }}>
                    <Row justify="space-between" align="middle">
                      <Text>Godkjent for booking</Text>
                      <Form.Item name="approved_for_booking" valuePropName="checked" noStyle>
                        <Switch defaultChecked />
                      </Form.Item>
                    </Row>
                    <Row justify="space-between" align="middle">
                      <Text>Vis i bedriftskatalogen</Text>
                      <Form.Item name="is_relevant" valuePropName="checked" noStyle>
                        <Switch defaultChecked />
                      </Form.Item>
                    </Row>
                  </Space>
                </Card>
              </Col>
            </Row>
          </Card>

          <Row justify="end">
            <Button type="primary" htmlType="submit" loading={loading}>
              Opprett bedrift
            </Button>
          </Row>
        </Space>
      </Form>
    </>
  );
};

export default CreateCompanyProfileAdmin;
